import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Divider,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { EventPrintTemplateViewModel } from '@api/bff/eventPrintTemplates';

type Props = {
  showPricing: boolean;
  templateType: string;
  templateName: string;
  templateOptions: EventPrintTemplateViewModel[];
  isLoadingTemplates: boolean;
  onToggleShowPricing: (value: boolean) => void;
  onChangeTemplateType: (value: string) => void;
  onChangeTemplateName: (value: string) => void;
};

export const GeneralSettingsContent = ({
  showPricing,
  templateType,
  templateName,
  templateOptions,
  isLoadingTemplates,
  onToggleShowPricing,
  onChangeTemplateType,
  onChangeTemplateName
}: Props) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        padding: { xs: 2, sm: 3, md: 4 },
        maxWidth: 800,
        margin: '0 auto'
      }}
    >
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                {t('settings:generalSettings.showPricing.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings:generalSettings.showPricing.description')}
              </Typography>
            </Box>
            <Switch
              checked={showPricing}
              onChange={e => onToggleShowPricing(e.target.checked)}
              color="primary"
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              {t('settings:generalSettings.templateType.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('settings:generalSettings.templateType.description')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={templateType || 'CERTIFICATES'}
              onChange={e => onChangeTemplateType(e.target.value)}
              disabled
              InputProps={{
                readOnly: true
              }}
            />
          </Box>

          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              {t('settings:generalSettings.templateName.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('settings:generalSettings.templateName.description')}
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={templateName}
                onChange={e => onChangeTemplateName(e.target.value)}
                displayEmpty
                disabled={isLoadingTemplates}
                endAdornment={
                  isLoadingTemplates ? (
                    <InputAdornment position="end" sx={{ mr: 2 }}>
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : null
                }
              >
                {templateOptions.length === 0 && !isLoadingTemplates && (
                  <MenuItem value="" disabled>
                    {t('settings:generalSettings.templateName.noTemplates')}
                  </MenuItem>
                )}
                {templateOptions.map(template => (
                  <MenuItem
                    key={template.templateName}
                    value={template.templateName}
                  >
                    {template.templateName}
                    {template.templateDescription &&
                      ` - ${template.templateDescription}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
